/**
 * @file EmployeeRowActions.tsx
 * @brief Per-row actions for company employees.
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  Download,
  Ellipsis,
  Mail,
  Pencil,
  RotateCcwClock,
  Shield,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import {
  useDeleteEmployee,
  useDownloadEmployeeData,
  useResendEmployeeWelcome,
  useUpdateEmployeeStatus,
} from "../api";
import type { CompanyEmployee } from "../schemas";

interface EmployeeRowActionsProps {
  employee: CompanyEmployee;
  onEdit: () => void;
  onViewHistory: () => void;
}

export function EmployeeRowActions({
  employee,
  onEdit,
  onViewHistory,
}: EmployeeRowActionsProps) {
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.id === employee.id;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const updateStatus = useUpdateEmployeeStatus();
  const remove = useDeleteEmployee();
  const resend = useResendEmployeeWelcome();
  const downloadData = useDownloadEmployeeData();

  const run = async (action: () => Promise<unknown>, title: string) => {
    try {
      await action();
      toast({ variant: "success", title });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Action failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  if (employee.status === "deleted") return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Employee actions">
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="h-4 w-4" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onViewHistory}>
            <RotateCcwClock className="h-4 w-4" /> Login history
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              run(
                () => resend.mutateAsync(employee.id),
                "Welcome email sent",
              )
            }
          >
            <Mail className="h-4 w-4" /> Resend welcome
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={downloadData.isPending}
            onClick={() =>
              run(
                () => downloadData.mutateAsync({ id: employee.id, email: employee.email }),
                "Data downloaded. Open README.txt in the zip first.",
              )
            }
          >
            <Download className="h-4 w-4" />
            {downloadData.isPending ? "Preparing data…" : "Download data"}
          </DropdownMenuItem>
          {!isSelf ? (
            <>
              <DropdownMenuSeparator />
              {employee.status === "suspended" ? (
                <DropdownMenuItem
                  onClick={() =>
                    run(
                      () =>
                        updateStatus.mutateAsync({
                          id: employee.id,
                          status: "active",
                        }),
                      "Employee reactivated",
                    )
                  }
                >
                  <Shield className="h-4 w-4" /> Reactivate
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() =>
                    run(
                      () =>
                        updateStatus.mutateAsync({
                          id: employee.id,
                          status: "suspended",
                        }),
                      "Employee suspended",
                    )
                  }
                >
                  <ShieldOff className="h-4 w-4" /> Suspend
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete employee?</AlertDialogTitle>
            <AlertDialogDescription>
              {employee.email} will be marked deleted and lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                run(
                  () => remove.mutateAsync(employee.id),
                  "Employee deleted",
                )
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
